// What one element dispatch measured (./element/pipeline.ts), as data on its failures; what one dispatch did (./index.ts
// `performAction`), as its record.
import type { OccluderLocation, OccluderNodes } from '../../surfaces/locate.ts'
import type { Strategy } from '../recovery/index.ts'
import type { ExecutableType } from './action.ts'
import type { Box, Point } from './element/measure.ts'
import type { Completion } from './element/pipeline.ts'
import type { ActionError, ActionErrorRecord, ForeignError, OpVerb } from './errors.ts'

/**
 * The dispatch stage that measured a failure's fact. `settle`: the target's settle (./element/settle-target.ts);
 * `reach`: the pointer reach (./element/reach.ts); `preGesture`: an authored click's read after its pre-click rest,
 * before its gesture (./element/pipeline.ts); `preEffect`: an arm's read after those and before its effect op;
 * `gesture`: the re-measure after a pointer gesture timed out (./element/pointer-action.ts); `effect`: after the
 * arm's effect op was issued; `remedy`: a read of the remedy its caller chose (./choice.ts).
 */
export const PHASES = ['settle', 'reach', 'preGesture', 'preEffect', 'gesture', 'effect', 'remedy'] as const
export type Phase = (typeof PHASES)[number]

/** A page element as evidence, read in the measuring task. `name`: its `aria-labelledby` text; `text`: its own; each
 * string cut at NODE_REF_CUT characters (./element/measure.ts). */
export const NODE_REF_CUT = 80
export interface NodeRef { tag: string; role: string | null; ariaLabel: string | null; name: string | null; text: string }

/** What drops T from hit-testing. `pointerEventsNone`: the top of the run of `none` T's own computed value continues
 * up its composed chain; `inert`: T's nearest inert ancestor-or-self. */
export const OFF_SWITCHES = ['pointerEventsNone', 'inert'] as const
export type OffSwitch = (typeof OFF_SWITCHES)[number]

/** The outermost switch on T's chain. */
export interface OffSwitchRef { by: OffSwitch; node: NodeRef }

/** How far the search for an escape layer read the switch's region: `capped` at its node cap, so an escape may exist. */
export const ESCAPE_SCANS = ['complete', 'capped'] as const
export type EscapeScan = (typeof ESCAPE_SCANS)[number]

/**
 * Why the pointer hits `hit` instead of the target T at its point.
 * - `layer`: `hit` is outside T's composed ancestors; `layer` is the child of the lowest common ancestor of `hit` and T
 *   on `hit`'s composed path, null when that is `hit`.
 * - `escape`: `hit` is T's ancestor, `offSwitch` drops T from hit-testing, and `layer` is the last visible element in
 *   document order that takes pointer events in the region the switch spans, outside T.
 * - `self`: `hit` is T's ancestor and the escape search found none; `escapeScan` null when there was no region to read.
 */
export type OccluderShape =
  | { kind: 'layer'; layer: NodeRef | null; offSwitch: null }
  | { kind: 'escape'; layer: NodeRef; offSwitch: OffSwitchRef }
  | { kind: 'self'; layer: null; offSwitch: OffSwitchRef | null; escapeScan: EscapeScan | null }

/**
 * What every occluder carries. `modal`: `hit`'s INERT_BACKGROUND root lies outside T (surfaces/roots.ts). `area`:
 * `hit`'s, as a fraction of the viewport. `pinned`: the fixed / sticky ancestor-or-self of `hit` outside T. `chrome`: a
 * scroll keeping T fully visible moves its point out of `pinned` (./element/measure.ts clearanceScroll). `mirror`: its
 * rrweb mirror ids and Surface lookup; null when the page has no recorder mirror.
 */
export interface OccluderCommon {
  hit: NodeRef
  modal: boolean
  area: number
  coversViewport: boolean
  pinned: Box | null
  point: Point
  chrome: boolean
  mirror: { nodes: OccluderNodes; location: OccluderLocation } | null
}

/** What the pointer hits at the target's `point` instead of it. */
export type OccluderFacts = OccluderCommon & OccluderShape

/** An occluder beside T: what a TargetObstructed names. */
export type Obstruction = Exclude<OccluderFacts, { kind: 'self' }>

/** How an occluder left the dispatch. `repositioned`: the reach scrolled clear of it and went on; `blocked`: an
 * attempt failed on it (TargetObstructed, or TargetNotHittable for `self`); its `decision` says what followed. */
export const OCCLUSION_ENDS = ['repositioned', 'blocked'] as const
export type OcclusionEnd = (typeof OCCLUSION_ENDS)[number]

/** What a dispatch asks its caller's chooser when a layer beside its target covers it before input (./choice.ts):
 * the target, and the attempt's failure naming the occluder. */
export interface ChoiceRequest { target: { selector: string }; failure: ActionError<'TargetObstructed'> }

/** The caller's remedy: click one control of the covering layer (a Playwright selector), press Escape, or none. */
export type Choice = { click: string } | { key: 'Escape' } | { none: true }
export type Chooser = (request: ChoiceRequest) => Promise<Choice>

/** Why a chosen control is no remedy, on fresh facts (./element/measure.ts ControlFit): it does not resolve to one
 * element, has no visible box, the pointer at its point in the viewport hits another element, it contains the target,
 * or it lies outside the layer covering the target. */
export const CHOICE_FAULTS = ['absent', 'ambiguous', 'unrendered', 'notHittable', 'containsTarget', 'outsideLayer'] as const
export type ChoiceFault = (typeof CHOICE_FAULTS)[number]

/**
 * A chooser's decision on `occluder`, as the covered attempt named it. `cleared`: the re-run was not blocked by it;
 * `persisted`: it blocked the re-run; `failed`: the remedy failed (`failure`), or the dispatch stopped (null) before the
 * re-run's reach decided; `input`: whether the remedy issued input ops (none when the occluder was gone by then);
 * `declined`: the choice was `none`; `invalidChoice`: the chosen control is no remedy (`fault`).
 */
export type Decided = { occluder: Obstruction; choice: Choice; waitedMs: number } & (
  | { outcome: 'cleared' | 'persisted'; input: boolean }
  | { outcome: 'failed'; input: boolean; failure: ActionErrorRecord | null }
  | { outcome: 'declined' }
  | { outcome: 'invalidChoice'; fault: ChoiceFault })

/** Why an occlusion that blocked an attempt went undecided: no chooser, the chooser threw (`error`), or no attempt was left. */
export type Undecided = { undecided: 'noChooser' | 'budget' } | { undecided: 'chooserError'; error: ForeignError }

export type OcclusionDecision = { decided: Decided } | Undecided

/**
 * One distinct occluder a dispatch's reads saw. `phase`, `atMs`: its first sighting; `occluder`: its latest, a located
 * one kept over a later unlocated sighting; `ended`: null when it neither blocked an attempt nor was scrolled clear of;
 * `decision`: what the dispatch did about an occluder that blocked an attempt before input (./index.ts), null when it
 * did not decide.
 */
export interface Occlusion { occluder: OccluderFacts; phase: Phase; atMs: number; ended: OcclusionEnd | null; decision: OcclusionDecision | null }

/** An op's part in its dispatch. `probe`: a read; `prep`: input that readies the effect (pointer approach, focus,
 * caret, a file dialog's opener); `effect`: the arm's own op; `remedy`: input that removes an obstacle, a recovery
 * strategy's or a chosen remedy's (./choice.ts). */
export const OP_ROLES = ['probe', 'prep', 'effect', 'remedy'] as const
export type OpRole = (typeof OP_ROLES)[number]

/** How an issued op failed (./driver.ts). `capped`: timed out on a budget the action deadline cut. */
export const OP_FAILURES = ['timeout', 'wall', 'capped', 'error'] as const
export type OpFailure = (typeof OP_FAILURES)[number]

/**
 * A run of `count` consecutive ops of one verb, role and outcome. `atMs`: the first's, since the dispatch started;
 * `ms`: elapsed, summed; `msEff`: the last's effective native timeout.
 */
export type OpFact = { verb: OpVerb; role: OpRole; atMs: number; count: number } & (
  | { outcome: 'notIssued' }
  | { outcome: 'ok' | OpFailure; ms: number; msEff: number })

/** The recovery re-run: when it started, and the first attempt's failure. */
export interface Remedy { strategy: Strategy; atMs: number; after: ActionErrorRecord }

/** `dispatched`: effect input reached the page (./index.ts `undispatchedFailure`). `stopped`: a throw ended the
 * dispatch (a run abort, a defect). */
export type DispatchOutcome = { performed: Completion } | { failed: ActionErrorRecord; dispatched: boolean } | { stopped: ForeignError }

/** One dispatch. `ops`: every op but an ok probe, which `probesOk` counts; `ms`: the whole dispatch. */
export interface DispatchRecord {
  action: ExecutableType
  ops: OpFact[]
  probesOk: number
  occlusions: Occlusion[]
  remedy: Remedy | null
  outcome: DispatchOutcome
  ms: number
}

/** Receives a dispatch's record, also when the dispatch throws. */
export type DispatchSink = (record: DispatchRecord) => void

/** One dispatch's collector: filled by its attempts' driver envs (./driver.ts) and its recovery plan (./index.ts).
 * `onEffect`: called before each of its effect ops is issued (./driver.ts). */
export interface DispatchLog {
  readonly startedAt: number; readonly ops: OpFact[]; probesOk: number; readonly occlusions: Occlusion[]; remedy: Remedy | null
  readonly onEffect: (() => void) | null
}

export const dispatchLog = (startedAt: number, onEffect: (() => void) | null = null): DispatchLog =>
  ({ startedAt, ops: [], probesOk: 0, occlusions: [], remedy: null, onEffect })

/** An ok probe is counted; any other op extends the previous entry's run of its verb, role and outcome. */
export function noteOp(log: DispatchLog, fact: OpFact): void {
  if (fact.outcome === 'ok' && fact.role === 'probe') {
    log.probesOk++
    return
  }
  const last = log.ops.at(-1)
  if (!last || last.verb !== fact.verb || last.role !== fact.role || last.outcome !== fact.outcome) {
    log.ops.push(fact)
    return
  }
  last.count += fact.count
  if (last.outcome !== 'notIssued' && fact.outcome !== 'notIssued') {
    last.ms += fact.ms
    last.msEff = fact.msEff
  }
}

// Distinct occluders kept per dispatch; past it, a new one replaces the latest that has not ended.
const OCCLUSIONS_MAX = 8

const nameOf = (n: NodeRef | null) => n && [n.tag, n.role, n.ariaLabel, n.name]
// An occluder's accessible naming: its nodes' names (a text may tick).
const namingKey = ({ kind, hit, layer, offSwitch }: OccluderFacts): string =>
  JSON.stringify([kind, nameOf(hit), nameOf(layer), offSwitch && [offSwitch.by, nameOf(offSwitch.node)]])
// An occluder's node identity: its Surface anchor's mirror id, else its naming.
const occluderKey = (o: OccluderFacts): string =>
  o.mirror && o.mirror.nodes.anchor !== null ? JSON.stringify([o.kind, o.mirror.nodes.anchor]) : namingKey(o)

/** Whether two sightings are of one occluder: one node, or one naming (a layer the page rendered again). */
export const sameOccluder = (a: OccluderFacts, b: OccluderFacts): boolean => occluderKey(a) === occluderKey(b) || namingKey(a) === namingKey(b)

const noted = (log: DispatchLog, occluder: OccluderFacts): Occlusion | undefined => log.occlusions.find((o) => sameOccluder(o.occluder, occluder))
const located = ({ mirror }: OccluderFacts): boolean => mirror?.location.state === 'located'

/** A sighting of `occluder`; a repeat of a noted one updates it, an `ended` given sets its end. */
export function noteOcclusion(log: DispatchLog, occluder: OccluderFacts, phase: Phase, ended: OcclusionEnd | null = null): void {
  const seen = noted(log, occluder)
  if (seen) {
    if (located(occluder) || !located(seen.occluder)) seen.occluder = occluder
    if (!seen.decision) seen.ended = ended ?? seen.ended
    return
  }
  if (log.occlusions.length >= OCCLUSIONS_MAX) {
    const open = log.occlusions.map((o) => o.ended).lastIndexOf(null)
    if (open < 0) return
    log.occlusions.splice(open, 1)
  }
  log.occlusions.push({ occluder, phase, atMs: Date.now() - log.startedAt, ended, decision: null })
}

/** The decision on the obstruction that blocked an attempt; an undecided one does not replace a decision. Kept also
 * when the cap dropped its sighting. */
export function decideOcclusion(log: DispatchLog, occluder: Obstruction, phase: Phase, decision: OcclusionDecision): void {
  const seen = noted(log, occluder)
  if (!seen) log.occlusions.push({ occluder, phase, atMs: Date.now() - log.startedAt, ended: 'blocked', decision })
  else if ('decided' in decision || !seen.decision) seen.decision = decision
}

/** The dispatch's occlusion its caller's chooser decided, with that decision; a dispatch asks at most once. */
export function decidedOcclusion(record: Pick<DispatchRecord, 'occlusions'>): { occlusion: Occlusion; decided: Decided } | null {
  for (const occlusion of record.occlusions) if (occlusion.decision && 'decided' in occlusion.decision) return { occlusion, decided: occlusion.decision.decided }
  return null
}

/** The record's occlusions that blocked an attempt or were decided. */
export const blockingOcclusions = ({ occlusions }: Pick<DispatchRecord, 'occlusions'>): Occlusion[] =>
  occlusions.filter((o) => o.ended === 'blocked' || o.decision !== null)

/** `prior` with `next` merged in: a later sighting of one occluder replaces it whole. */
export function mergeOcclusions(prior: readonly Occlusion[], next: readonly Occlusion[]): Occlusion[] {
  const merged = [...prior]
  for (const o of next) {
    const at = merged.findIndex((m) => sameOccluder(m.occluder, o.occluder))
    if (at < 0) merged.push(o)
    else merged[at] = o
  }
  return merged
}

/** Whether a chosen remedy's own log issued input. */
export const issuedInput = (run: DispatchLog): boolean => run.ops.some((op) => op.role !== 'probe' && op.outcome !== 'notIssued')

/** A chosen remedy's own log (started with the dispatch's), merged after it: its input as `remedy`, its sightings as
 * phase `remedy`. */
export function mergeChoiceRun(log: DispatchLog, run: DispatchLog): void {
  log.probesOk += run.probesOk
  for (const op of run.ops) noteOp(log, { ...op, role: op.role === 'probe' ? 'probe' : 'remedy' })
  for (const o of run.occlusions) noteOcclusion(log, o.occluder, 'remedy', o.ended)
}

/** The log's record as of now: its entries copied, so later merges leave it unchanged. */
export const dispatchRecord = (action: ExecutableType, log: DispatchLog, outcome: DispatchOutcome): DispatchRecord =>
  ({ action, ops: log.ops.map((op) => ({ ...op })), probesOk: log.probesOk, occlusions: log.occlusions.map((o) => ({ ...o })), remedy: log.remedy, outcome, ms: Date.now() - log.startedAt })
